import * as React from 'react';

import { MarkdownText } from '@/components/common/MarkdownText';
import { useTaskGuidelines } from '@/hooks/useTaskGuidelines';

interface TaskGuidelinesProps {
  matterId: string;
  taskType: string;
}

/**
 * Displays workflow-level guidelines from firm and matter configurations.
 */
export const TaskGuidelines: React.FC<TaskGuidelinesProps> = ({
  matterId,
  taskType,
}) => {
  const { guidelines, isLoading } = useTaskGuidelines(matterId, taskType);

  if (isLoading) {
    return null;
  }

  // Extract and join values (multiple workflows may apply to same task type)
  const configText = (guidelines ?? [])
    .map((g) => g.firm_config_summary)
    .filter(Boolean)
    .join(' · ');
  const firmNotesText = (guidelines ?? [])
    .map((g) => g.firm_notes)
    .filter(Boolean)
    .join(' · ');
  const caseText = (guidelines ?? [])
    .map((g) => g.matter_notes)
    .filter(Boolean)
    .join(' · ');

  const hasFirmGuidelines = configText || firmNotesText;
  const hasAnyGuidelines = hasFirmGuidelines || caseText;

  if (!hasAnyGuidelines) {
    return null;
  }

  return (
    <div className="mt-4 bg-gray-50 dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 rounded-lg p-3 shadow-sm">
      <h3 className="text-xs font-semibold text-gray-600 dark:text-zinc-400 uppercase tracking-wider mb-2">
        Guidelines
      </h3>

      <div className="space-y-1">
        {/* Firm Guidelines */}
        <div className="flex items-start gap-2">
          <span className="text-xs font-medium text-gray-500 dark:text-zinc-500 uppercase tracking-wide shrink-0">
            Firm:
          </span>
          {hasFirmGuidelines ? (
            <div className="flex flex-col gap-1">
              {configText && (
                <span className="text-xs font-medium text-gray-900 dark:text-zinc-100">
                  {configText}
                </span>
              )}
              {firmNotesText && (
                <MarkdownText
                  content={firmNotesText}
                  className="text-xs font-medium text-gray-900 dark:text-zinc-100"
                  plainTextClassName="whitespace-pre-wrap"
                  markdownClassName="prose prose-xs max-w-none prose-slate dark:prose-invert prose-p:my-0 prose-p:font-medium prose-p:text-xs"
                />
              )}
            </div>
          ) : (
            <span className="text-xs font-medium text-gray-500 dark:text-zinc-500 italic">
              None
            </span>
          )}
        </div>

        {/* Case-Specific Guidelines */}
        <div className="flex items-start gap-2">
          <span className="text-xs font-medium text-gray-500 dark:text-zinc-500 uppercase tracking-wide shrink-0">
            Case:
          </span>
          {caseText ? (
            <MarkdownText
              content={caseText}
              className="text-xs font-medium text-gray-900 dark:text-zinc-100"
              plainTextClassName="whitespace-pre-wrap"
              markdownClassName="prose prose-xs max-w-none prose-slate dark:prose-invert prose-p:my-0 prose-p:font-medium prose-p:text-xs"
            />
          ) : (
            <span className="text-xs font-medium text-gray-500 dark:text-zinc-500 italic">
              None
            </span>
          )}
        </div>
      </div>
    </div>
  );
};
